"use client";

import Image from "next/image";
import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

// 特性配置（用于默认翻译模式）
const DEFAULT_FEATURES = [
	{ titleKey: "certificationsTitle", descKey: "certificationsDesc" },
	{ titleKey: "expertiseTitle", descKey: "expertiseDesc" },
	{ titleKey: "transparentProcessTitle", descKey: "transparentProcessDesc" },
] as const;

// 默认图片配置 - 2x2 网格布局
const DEFAULT_QUALITY_IMAGES = [
	"/image/new-version/machining/five-axis/quality/precision-cnc-machining-guaranteed-quality-01.webp",
	"/image/new-version/machining/five-axis/quality/precision-cnc-machining-guaranteed-quality-02.webp",
	"/image/new-version/machining/five-axis/quality/precision-cnc-machining-guaranteed-quality-03.jpg",
	"/image/new-version/machining/five-axis/quality/precision-cnc-machining-guaranteed-quality-04.jpg",
];

// 自定义特性项接口
export interface QualityFeature {
	title: string;
	description: string;
}

// 自定义内容接口
export interface QualityCustomContent {
	title: string;
	description: string;
	features: QualityFeature[];
	imageAlt?: string;
}

// 组件 Props 接口
export interface QualityProps {
	translationNamespace?: string;
	customContent?: QualityCustomContent;
	customImages?: string[];
	showButton?: boolean;
	buttonText?: string;
	buttonHref?: string;
	// 新增：标题是否单独放在顶部（居中显示）
	titleOnTop?: boolean;
	/** 区块背景色，如 "#ffffff"、"#FCFFF4"，不传时默认 "#fcfff4" */
	bgColor?: string;
	// 新增：小标题颜色（默认绿色，可设为黑色）
	featureTitleColor?: "green" | "black";
	className?: string;
}

export default function Quality({
	translationNamespace = "fiveAxisQuality",
	customContent,
	customImages,
	showButton = true,
	buttonText,
	buttonHref = "/quality-assurance",
	titleOnTop = false,
	bgColor = "#fcfff4",
	featureTitleColor = "green",
	className,
}: QualityProps) {
	const t = useTranslations(translationNamespace);

	// 判断是否使用自定义内容
	const useCustomContent = !!customContent;

	// 使用自定义图片或默认图片
	const images = customImages || DEFAULT_QUALITY_IMAGES;

	// 获取图片 alt 文本
	const imageAlt = customContent?.imageAlt || t("imageAlt");

	// 小标题颜色类名
	const featureTitleClass = featureTitleColor === "black" ? "text-black" : "text-themePrimary400";

	// 获取标题和描述
	const title = useCustomContent ? customContent.title : t("title");
	const description = useCustomContent ? customContent.description : t("description");

	// 背景色：支持通过 prop 传入，默认浅绿色
	const sectionBgColor = bgColor ?? "#fcfff4";

	return (
		<section
			className={cn("w-full py-8 sm:py-10 md:py-12 lg:py-16", className)}
			style={{ backgroundColor: sectionBgColor }}
		>
			<div className="container">
				{/* 标题单独在顶部时的布局 */}
				{titleOnTop && (
					<div className="mx-auto mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{title}
						</h2>
						<p className="text-sm leading-relaxed text-gray-900 md:text-base">{description}</p>
					</div>
				)}

				{/* 使用 items-stretch 让左右两侧高度一致 */}
				<div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-12 xl:gap-16">
					{/* 左侧图片网格 - 2x2 布局，高度自适应 */}
					<div className="w-full lg:w-[45%]">
						<div className="grid h-full grid-cols-2 gap-3 md:gap-4">
							{images.map((src, index) => (
								<div key={index} className="relative w-full overflow-hidden">
									<Image
										src={src}
										alt={`${imageAlt} ${index + 1}`}
										fill
										className="object-cover"
										sizes="(max-width: 1024px) 50vw, 22vw"
									/>
								</div>
							))}
						</div>
					</div>

					{/* 右侧内容区 - 使用 flex 布局平均分配空间 */}
					<div className="flex w-full flex-col lg:w-[55%]">
						{/* 标题（非顶部模式时显示） */}
						{!titleOnTop && (
							<>
								<h2 className="mb-3 text-2xl font-bold leading-tight text-[#67A526] sm:mb-4 sm:text-[30px] md:text-4xl">
									{title}
								</h2>
								<p className="mb-6 text-sm leading-relaxed text-black md:text-base">{description}</p>
							</>
						)}

						{/* 特性列表 - flex-1 占据剩余空间，justify-between 平均分配 */}
						<div
							className={`flex flex-1 flex-col justify-between gap-6 md:gap-12 ${showButton ? "mb-6" : ""}`}
						>
							{useCustomContent
								? customContent.features.map((feature, index) => (
										<div key={index}>
											{/* 特性标题 */}
											<h3 className={`mb-1.5 text-base font-semibold ${featureTitleClass} sm:text-lg`}>
												{feature.title}
											</h3>
											{/* 特性描述 */}
											<p className="text-sm leading-relaxed text-black md:text-base">{feature.description}</p>
										</div>
									))
								: DEFAULT_FEATURES.map((feature, index) => (
										<div key={index}>
											{/* 特性标题 */}
											<h3
												className={`mb-1.5 text-base font-semibold text-[#67A526] ${featureTitleClass} sm:text-lg`}
											>
												{t(feature.titleKey)}
											</h3>
											{/* 特性描述 */}
											<p className="text-sm leading-relaxed text-black md:text-base">{t(feature.descKey)}</p>
										</div>
									))}
						</div>

						{/* Learn More 按钮 - 可选，支持外链新窗口打开 */}
						{showButton &&
							(buttonHref.startsWith("http") ? (
								<a
									href={buttonHref}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex w-fit items-center justify-center gap-1 text-sm font-bold text-themePrimary400 transition-colors duration-200 hover:text-mainColor hover:underline sm:text-base"
								>
									{buttonText || t("learnMore")}
									<svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M7 17L17 7M17 7H7M17 7v10"
										/>
									</svg>
								</a>
							) : (
								<Link
									href={buttonHref}
									className="inline-flex w-fit items-center justify-center gap-1 text-sm font-bold text-themePrimary400 transition-colors duration-200 hover:text-mainColor hover:underline sm:text-base"
								>
									{buttonText || t("learnMore")}
									<svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M7 17L17 7M17 7H7M17 7v10"
										/>
									</svg>
								</Link>
							))}
					</div>
				</div>
			</div>
		</section>
	);
}
